import { isEmpty as emberIsEmpty } from '@ember/utils';

type Falsy = false | 0 | '' | null | undefined;
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Required for type inference in or() helper
type MaybeTruthy = any;
type TruthConvert<T> = T extends Falsy ? false : true;
type FirstTruthy<T extends MaybeTruthy[]> = T extends [infer Item]
  ? Item
  : T extends [infer Head, ...infer Tail]
    ? TruthConvert<Head> extends true
      ? Head
      : TruthConvert<Head> extends false
        ? FirstTruthy<Tail>
        : Exclude<Head, Falsy> | FirstTruthy<Tail>
    : undefined;
const truthConvert = (value: MaybeTruthy): boolean => {
  return Boolean(value);
};

export const or = <T extends MaybeTruthy[]>(...args: T): FirstTruthy<T> => {
  for (let i = 0, len = args.length; i < len; i++) {
    if (truthConvert(args[i]) === true) {
      return args[i];
    }
  }
  return args[args.length - 1];
};

// `and`/`or`/`not` stay local: the Ember built-ins use Handlebars truthiness (an
// empty array is falsy) and `and`/`or` return the operand value, not a boolean.
// `eq`/`notEq`/`gt`/`gte`/`lt`/`lte` were dropped for the typed built-ins.
export const and = (...values: unknown[]): boolean => values.every(Boolean);
export const not = <A>(a?: A): boolean => !a;
export const truthy = (a: unknown) => Boolean(a);
export const isEmpty = emberIsEmpty;
export const contains = (a: unknown, b: unknown): boolean =>
  (a as string | unknown[]).includes(b as never);
export const has = <T>(set: Set<T> | null | undefined, value: T): boolean =>
  Boolean(set?.has(value));

/** Methods for manipulating strings. Only use if CSS lowercase is not an option */
export const lowercase = (string = '') => {
  if (typeof string !== 'string') {
    throw new TypeError(`Expected a string, got a ${typeof string}`);
  }

  return string.toLowerCase();
};

export const join = (array: (string | number)[] = [], separator = '') => {
  return array.join(separator);
};

export const split = (value: string): string[] => {
  if (!value) {
    return [];
  }
  return value.split(',').map((v) => v.trim());
};

export function humanizeDate(dateString?: string) {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    const dateTime = date.getTime();

    if (Number.isNaN(dateTime)) {
      return '';
    }

    const now = new Date();
    const diffMs = now.getTime() - dateTime;

    if (diffMs < 0) {
      return 'in the future';
    }

    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        return diffMinutes <= 1 ? 'just now' : `${diffMinutes} minutes ago`;
      }
      return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    } else if (diffDays === 1) {
      return 'yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
    } else {
      const months = Math.floor(diffDays / 30);
      return months === 1 ? '1 month ago' : `${months} months ago`;
    }
  } catch {
    return '';
  }
}
